import type { Wildfire } from '@/types';
import { hashString } from '@/utils/format';

// ---------------------------------------------------------------------------
// NASA EONET v3 — Earth Observatory Natural Event Tracker
// https://eonet.gsfc.nasa.gov/api/v3/events
// ---------------------------------------------------------------------------

const EONET_URL = 'https://eonet.gsfc.nasa.gov/api/v3/events';
const TIMEOUT_MS = 20_000;
const WILDFIRE_CATEGORY_ID = 'wildfires';

interface EONETGeometry {
  magnitudeValue: number | null;
  magnitudeUnit: string | null;
  date: string;
  type: string;
  coordinates: [number, number]; // [lon, lat]
}

interface EONETSource {
  id: string;
  url: string;
}

interface EONETEvent {
  id: string;
  title: string;
  description: string | null;
  link: string;
  closed: string | null;
  categories: { id: string; title: string }[];
  sources: EONETSource[];
  geometry: EONETGeometry[];
}

interface EONETResponse {
  title: string;
  description: string;
  link: string;
  events: EONETEvent[];
}

/**
 * Fetch active wildfire events from NASA EONET v3.
 * Filters for wildfire category events from the last 3 days.
 */
export async function fetchWildfires(): Promise<Wildfire[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const params = new URLSearchParams({
      days: '3',
      status: 'open',
      category: WILDFIRE_CATEGORY_ID,
    });

    const res = await fetch(`${EONET_URL}?${params}`, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`NASA EONET responded with ${res.status}`);
    }

    const data: EONETResponse = await res.json();
    return parseEONETEvents(data.events ?? []);
  } catch (err) {
    console.error('[NASA-EONET] Failed to fetch wildfires', err);
    // Try without the category filter as fallback
    return fetchFallback();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fallback: fetch all events and filter for wildfires client-side.
 */
async function fetchFallback(): Promise<Wildfire[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const params = new URLSearchParams({
      days: '3',
      status: 'open',
    });

    const res = await fetch(`${EONET_URL}?${params}`, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`NASA EONET fallback responded with ${res.status}`);
    }

    const data: EONETResponse = await res.json();

    // Filter for wildfire events manually
    const wildfireEvents = (data.events ?? []).filter((e) =>
      e.categories.some(
        (c) => c.id === WILDFIRE_CATEGORY_ID || c.title.toLowerCase().includes('fire')
      )
    );

    return parseEONETEvents(wildfireEvents);
  } catch (err) {
    console.error('[NASA-EONET] Fallback also failed', err);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Parse EONET events into Wildfire records.
 * Each EONET event can have multiple geometry points (tracking over time).
 * We take the most recent geometry point for each event.
 */
function parseEONETEvents(events: EONETEvent[]): Wildfire[] {
  if (!Array.isArray(events)) return [];

  const wildfires: Wildfire[] = [];

  for (const event of events) {
    if (!event.geometry?.length) continue;

    // Sort geometries by date descending, take the most recent
    const sortedGeom = [...event.geometry].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    const latestGeom = sortedGeom[0];
    if (!latestGeom.coordinates || latestGeom.coordinates.length < 2) continue;

    const [lon, lat] = latestGeom.coordinates;

    // Derive satellite name from sources if available
    const satellite = deriveSatellite(event.sources);

    // Estimate brightness / FRP from magnitude if available
    const magnitude = latestGeom.magnitudeValue ?? 0;
    const brightness = magnitude > 0 ? magnitude : estimateBrightness(event.title);

    // Extract rough country from event title
    const country = extractCountryFromTitle(event.title);

    wildfires.push({
      id: event.id ?? `eonet-${hashString(event.title + lat + lon)}`,
      lat,
      lon,
      brightness,
      confidence: magnitude > 0 ? 80 : 50, // Higher confidence if magnitude data present
      frp: magnitude > 0 ? magnitude : 0,
      timestamp: latestGeom.date ?? new Date().toISOString(),
      satellite,
      country,
    });
  }

  return wildfires.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

/**
 * Derive satellite name from EONET source IDs.
 */
function deriveSatellite(sources: EONETSource[]): string {
  if (!sources?.length) return 'EONET';

  const knownSatellites: Record<string, string> = {
    'InciWeb': 'InciWeb',
    'MODIS': 'MODIS',
    'VIIRS': 'VIIRS',
    'EO': 'Earth Observatory',
    'MBFIRE': 'MODIS/VIIRS',
    'PDC': 'PDC',
    'GDACS': 'GDACS',
    'IDC': 'IDC',
    'NATICE': 'NATICE',
    'CEMS': 'Copernicus',
  };

  for (const src of sources) {
    const upper = src.id.toUpperCase();
    for (const [key, name] of Object.entries(knownSatellites)) {
      if (upper.includes(key.toUpperCase())) return name;
    }
  }

  return sources[0]?.id ?? 'EONET';
}

/**
 * Heuristic brightness estimate based on event title keywords.
 */
function estimateBrightness(title: string): number {
  const lower = title.toLowerCase();
  if (lower.includes('large') || lower.includes('major') || lower.includes('complex')) return 400;
  if (lower.includes('moderate')) return 320;
  return 300; // default baseline
}

/**
 * Extract a rough country name from EONET event titles.
 * Titles often follow the pattern "Fire Name, Country" or "Fire Name, State, Country".
 */
function extractCountryFromTitle(title: string): string | undefined {
  if (!title) return undefined;

  // Common patterns: "Palisades Fire, California, United States"
  const parts = title.split(',').map((s) => s.trim());
  if (parts.length >= 2) {
    return parts[parts.length - 1];
  }

  return undefined;
}
